import React, { useMemo, useState } from "react";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import Conditions from "./../../switch/conditions";
import { v4 as uuid } from "uuid";
import FullModal from "../../../full-modal";
import type { Condition } from "@/workflow/types/Switch";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import type { NodeData } from "@/workflow_api/types/workflow";

import {
  parseType,
  useGetVariables,
} from "@/workflow_api/hooks/variables/useGetVariables";
import { Operations } from "./../../if/operations";
import { Icon } from "@elaniintc/goes-design-system";
import Output from "../output";
import { useGetNodeTestInputExecution } from "@/workflow_api/services/nodes.sandbox.api";

interface Props {
  initialTitle?: string;
}

export const SwitchDetails: React.FC<Props> = ({
  initialTitle = "Operador Switch",
}) => {
  const { modalState, setModalState, selectedNodeId, setSelectedNodeId } =
    useUIStore();
  const { getNodeById } = useWorkflowStore();
  const node = getNodeById(selectedNodeId || "");
  const nodeData = node?.data as unknown as NodeData;
  const [title, setTitle] = useState<string>(
    nodeData.displayName || initialTitle,
  );

  const { variables: nodeVariables, isLoading } = useGetVariables();

  const [conditions, setConditions] = useState<Condition[]>(() => {
    const hasInputs =
      nodeData.inputs && Object.keys(nodeData.inputs).length > 0;
    if (!hasInputs)
      return [
        {
          id: `condition_1`,
          label: "Salida 1",
          variable: null,
          operation: null,
          comparativeValue: null,
        },
      ];
    return Object.values(nodeData.inputs || {}).map((c, index) => {
      const condition = c.conditions?.[0];
      const conditionType = condition?.type;
      const inputType = condition?.input?.type;
      const hasVariable = condition?.input?.source_connections?.length !== 0;
      const optionalInput = condition?.optional_input?.value;
      return {
        id: `condition_${index + 1}`,
        label:
          nodeData.connections?.[`condition_${index + 1}`].display_name || "",
        variable: hasVariable
          ? {
              node_id:
                condition?.input?.source_connections?.[0].source_node_id || "",
              id:
                condition?.input?.source_connections?.[0]?.field?.id ||
                condition?.input?.source_connections?.[0].source_node_id ||
                "",
              name:
                condition?.input?.source_connections?.[0]?.field?.name || "",
              type:
                parseType(
                  condition?.input?.source_connections?.[0].field?.type ===
                    "file"
                    ? condition?.input?.source_connections?.[0].field?.config
                        ?.multiple
                      ? "array"
                      : "string"
                    : parseType(
                        condition?.input?.source_connections?.[0].field?.type ||
                          inputType?.toLowerCase() ||
                          "",
                      ),
                ) || "",
              ref_name: "",
            }
          : null,
        operation: condition
          ? Operations[inputType?.toLowerCase() || ""].find((op) => {
              return op.name === conditionType;
            }) || null
          : null,
        comparativeValue: optionalInput?.toString() || null,
      };
    });
  });

  const onAddCondition = () => {
    setConditions((prev) => [
      ...prev,
      {
        id: uuid(),
        variable: null,
        operation: null,
        comparativeValue: null,
        label: "",
      },
    ]);
  };

  const onDeleteCondition = (id: string) => {
    setConditions((prev) => prev.filter((c) => c.id !== id));
  };

  const onUpdateCondition = (id: string, condition: Condition) => {
    setConditions((prev) => prev.map((c) => (c.id === id ? condition : c)));
  };

  const areConditionsValid = useMemo(() => {
    return conditions.every(
      (c) =>
        c.variable &&
        c.operation &&
        (!c.operation.needsComparativeValue || c.comparativeValue),
    );
  }, [conditions]);

  const {
    data,

    isPending: isFetchingResults,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetNodeTestInputExecution(selectedNodeId!);

  return (
    <>
      <FullModal
        isOpen={modalState === "test-input"}
        onClose={() => {
          setSelectedNodeId();
          setModalState("idle");
        }}
        isSaveValid={areConditionsValid}
        onSave={() => {
          //TODO save test input logic
          setSelectedNodeId();
          setModalState("idle");
        }}
        actionLabel="Probar"
      >
        <ThreeColumnLayout
          disableContent
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
          }}
          centerHeader={{
            icon: "arrows-cross",
            title: title,
            color: "neutral",
            editable: true,
            onTitleChange: (newTitle: string) => setTitle(newTitle),
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContent={
            <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
              {/* <Variables sourceNodeId={selectedNodeId!} /> */}
            </div>
          }
          centerContent={
            <>
              {isLoading && (
                <div className="w-full flex items-center justify-center min-h-[300px]">
                  <Icon
                    name="spinner-third"
                    className="animate-spin text-on-disabled"
                    type="far"
                  />
                </div>
              )}
              {!isLoading && (
                <Conditions
                  conditions={conditions}
                  areConditionsValid={areConditionsValid}
                  onAddCondition={onAddCondition}
                  onDeleteCondition={onDeleteCondition}
                  onUpdateCondition={onUpdateCondition}
                  variables={nodeVariables}
                />
              )}
            </>
          }
          rightContent={
            <>
              <Output
                data={
                  data?.pages
                    ?.flatMap((p) => {
                      console.log(p);
                      return p.data;
                    })
                    .flatMap((d) => d.outputs) || []
                }
                loadMore={fetchNextPage}
                hasMore={hasNextPage}
                isLoadingMore={isFetchingNextPage}
                isLoading={isFetchingResults}
              />
            </>
          }
        />
      </FullModal>
    </>
  );
};
